import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, Navigation, Plus, Loader2, Clock, User, Pencil } from "lucide-react";
import { EditContactSheet } from "@/components/mobile/EditContactSheet";
import { LogEventSheet } from "@/components/mobile/LogEventSheet";
import { MobileDeals } from "@/components/mobile/MobileDeals";
import { Section } from "@/components/mobile/Section";

const addressOf = (c) => [c.address, c.city, [c.state, c.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");

export default function MobileCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () => api.get(`/crm/customers/${id}`).then(({ data }) => setData(data)).catch(() => { toast.error("Customer not found"); navigate("/mobilecrm/customers"); });
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  const c = data.customer;
  const addr = addressOf(c);
  const mapsHref = c.lat && c.lng ? `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
  const calls = [...data.calls].sort((a, b) => (b.call_date || "").localeCompare(a.call_date || ""));

  return (
    <div className="pb-6" data-testid="m-customer">
      <div className="bg-white border-b border-zinc-200 px-4 pt-3 pb-4">
        <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/mobilecrm/customers"))} data-testid="m-cust-back" className="flex items-center gap-1 text-sm text-zinc-500 mb-2"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="flex items-start gap-2">
          <h1 className="flex-1 font-display font-bold text-xl text-[#0F172A] leading-tight" data-testid="m-cust-name">{c.company_name}</h1>
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border shrink-0 ${c.is_prospect ? "border-[#3B82F6] text-[#3B82F6]" : "border-[#D4AF37] text-[#B8860B]"}`}>{c.is_prospect ? "Prospect" : "Customer"}</span>
        </div>
        {addr && <p className="text-sm text-zinc-500 mt-1">{addr}</p>}
        <p className="text-xs text-zinc-400 mt-1">{c.assigned_rep_name ? `Rep: ${c.assigned_rep_name}` : "Unassigned"}{c.account_number ? ` · Acct ${c.account_number}` : ""}</p>
        <div className="grid grid-cols-4 gap-2 mt-4">
          <Action href={c.phone ? `tel:${c.phone}` : null} onClick={() => setSheet("Phone Call")} icon={Phone} label="Call" testid="m-act-call" />
          <Action href={c.email ? `mailto:${c.email}` : null} onClick={() => setSheet("Email")} icon={Mail} label="Email" testid="m-act-email" />
          <Action href={addr || c.lat ? mapsHref : null} icon={Navigation} label="Directions" testid="m-act-directions" external />
          <button onClick={() => setSheet("In Person Visit")} data-testid="m-act-log" className="flex flex-col items-center gap-1 py-2.5 bg-[#D4AF37] text-[#0F172A] text-[11px] font-semibold"><Plus className="h-5 w-5" />Log</button>
        </div>
      </div>

      <Section title="Contact" action={<button onClick={() => setEditing(true)} data-testid="m-contact-edit" className="flex items-center gap-1 text-xs font-semibold text-[#0F172A]"><Pencil className="h-3.5 w-3.5" /> Edit</button>}>
        {c.contact_name || c.phone || c.email ? (
          <div className="space-y-1.5 text-sm" data-testid="m-cust-contact">
            {c.contact_name && <p className="flex items-center gap-2 text-[#0F172A]"><User className="h-4 w-4 text-zinc-400" />{c.contact_name}</p>}
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-[#0F172A]"><Phone className="h-4 w-4 text-zinc-400" />{c.phone}</a>}
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-[#0F172A] break-all"><Mail className="h-4 w-4 text-zinc-400" />{c.email}</a>}
            {c.secondary_contact && <p className="text-xs text-zinc-500 pt-1">Also: {c.secondary_contact}{c.secondary_phone ? ` · ${c.secondary_phone}` : ""}</p>}
          </div>
        ) : <p className="text-sm text-zinc-400">No contact details yet — tap Edit to add them.</p>}
        {c.notes && <p className="mt-3 text-sm text-zinc-600 whitespace-pre-line border-t border-zinc-100 pt-3">{c.notes}</p>}
      </Section>

      <MobileDeals customerId={id} deals={data.deals} onChange={load} />

      <Section title={`History (${calls.length})`}>
        {calls.length === 0 ? <p className="text-sm text-zinc-400">No events logged yet.</p> : (
          <div className="divide-y divide-zinc-100" data-testid="m-cust-history">
            {calls.map((e) => (
              <div key={e.id} className="py-2.5" data-testid={`m-event-${e.id}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-[#0F172A]">{e.event_type || "Phone Call"}</span>
                  <span className="text-zinc-400 flex items-center gap-1"><Clock className="h-3 w-3" />{(e.call_date || "").slice(0, 10)}{e.visit_time ? ` ${e.visit_time}` : ""}</span>
                </div>
                {(e.contact || e.outcome) && <p className="text-xs text-zinc-500 mt-0.5">{[e.contact, e.outcome].filter(Boolean).join(" · ")}</p>}
                {e.notes && <p className="text-sm text-zinc-700 mt-0.5">{e.notes}</p>}
                {e.follow_up_date && <p className="text-[11px] text-[#B8860B] mt-0.5">Follow-up {e.follow_up_date.slice(0, 10)}{e.follow_up_done ? " · done" : ""}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {editing && <EditContactSheet customer={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {sheet && <LogEventSheet customerId={id} defaultType={sheet} defaultContact={c.contact_name || ""} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); load(); }} />}
    </div>
  );
}

function Action({ href, onClick, icon: Icon, label, testid, external }) {
  const cls = "flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold border";
  if (!href) return <span className={`${cls} border-zinc-200 text-zinc-300`} data-testid={`${testid}-disabled`}><Icon className="h-5 w-5" />{label}</span>;
  return (
    <a href={href} onClick={onClick} target={external ? "_blank" : undefined} rel="noreferrer" data-testid={testid} className={`${cls} border-[#0F172A] text-[#0F172A] active:bg-zinc-100`}>
      <Icon className="h-5 w-5" />{label}
    </a>
  );
}

